/**
 * SB2-W-15 — small fetch helper for the notifications/v1 surface.
 *
 * Mirrors the `authFetch` pattern (the `@aeriosiq/api-client` axios
 * instance is hard-wired to the LYSE base URL; notifications now live
 * at their own base after OQ-SB2-B2(b)). Errors carry the parsed JSON
 * body so the UI can surface `reason: '…'` codes inline.
 */
import { getNotificationsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface NotificationsFetchError extends Error {
  status: number;
  body: unknown;
}

interface NotificationsFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function notificationsHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function notificationsFetch<T>(
  path: string,
  opts: NotificationsFetchOptions = {},
): Promise<T> {
  const headers = notificationsHeaders();
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (body !== undefined) {
    init.body = body;
  }
  const res = await fetch(`${getNotificationsBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // ignore — non-JSON error bodies still surface the status.
    }
    const err = new Error(
      `notifications fetch failed: ${path} → ${String(res.status)}`,
    ) as NotificationsFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}
